import { type Thread, useStores } from '@littlebird/sdk';
import { format } from 'date-fns/format';
import { isToday } from 'date-fns/isToday';
import { isYesterday } from 'date-fns/isYesterday';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { logError } from '@/lib/errorLogging';

import { contextChatStore } from './context-chat.state';

const RECENT_LIMIT = 20;

interface ChatGroup {
  label: string;
  chats: Thread[];
}

export const RecentGeneralChatsMenu = observer(
  function RecentGeneralChatsMenu() {
    const { threads } = useStores();

    useEffect(() => {
      threads
        .loadRecentThreadsByOrigin('meetings_overview', RECENT_LIMIT)
        .catch((err) => {
          logError(err, 'Failed to load recent meetings-overview chats', {
            context: 'RecentGeneralChatsMenu:loadRecentThreadsByOrigin',
          });
        });
    }, [threads]);

    const chats = threads.recentChatsByOrigin(
      'meetings_overview',
      RECENT_LIMIT
    );
    if (chats.length === 0) return null;

    const groups = groupChatsByDay(chats);
    const activeThreadId = contextChatStore.activeSession?.threadId;

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <IconButton
            size="sm"
            variant="ghost"
            className="text-foreground-muted"
            strokeWidth={1.8}
            icon={Icons.history}
            aria-label="Recent chats"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="max-h-96 w-72"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {groups.map((group) => (
            <DropdownMenu.Group key={group.label}>
              <DropdownMenu.Label className="py-1 text-xs">
                {group.label}
              </DropdownMenu.Label>
              {group.chats.map((chat) => (
                <DropdownMenu.Item
                  key={chat.id}
                  data-selected={chat.id === activeThreadId}
                  onSelect={() => {
                    void contextChatStore.openGeneralChat(chat.id);
                  }}
                >
                  <span className="truncate no-record">
                    {chat.title?.trim() || 'New chat'}
                  </span>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Group>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);

function groupChatsByDay(chats: Thread[]): ChatGroup[] {
  const groups: ChatGroup[] = [];
  let currentKey = '';

  for (const chat of chats) {
    const key = format(chat.lastMessageAt, 'yyyy-MM-dd');
    if (key !== currentKey) {
      currentKey = key;
      groups.push({ label: dayLabel(chat.lastMessageAt), chats: [] });
    }
    groups[groups.length - 1].chats.push(chat);
  }

  return groups;
}

function dayLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMM d, yyyy');
}
